import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import { ConfirmDeleteDialog } from "@pgs-core/ui/components/confirm-dialog";
import { ErrorAlert } from "@pgs-core/ui/components/error-alert";
import { formatTimestamp } from "@pgs-core/portal-lib/format";
import { createForm, deleteForm, type Form } from "../lib/forms";
import type { ApiClient } from "../lib/api";

interface FormsListCardProps {
  api: ApiClient;
  /** Forms from the parent's query; null while still loading. */
  forms: Form[] | null;
  /** The create dialog is opened from the page header, so the parent owns the flag. */
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
  /** A form was created (the card navigates to its builder). */
  onCreated: () => void;
  /** A form (and its submissions) was deleted. */
  onDeleted: () => void;
}

/** The "Your forms" table with its create dialog and delete confirmation. */
export function FormsListCard({
  api,
  forms,
  createOpen,
  onCreateOpenChange,
  onCreated,
  onDeleted,
}: FormsListCardProps) {
  const navigate = useNavigate();

  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Form | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");

  // The parent opens the dialog (page-header button) — reset the form each time.
  useEffect(() => {
    if (createOpen) {
      setNewName("");
      setCreateError("");
    }
  }, [createOpen]);

  const create = async () => {
    if (!newName.trim()) {
      setCreateError("Enter a form name.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const created = await createForm(api, newName.trim());
      onCreateOpenChange(false);
      onCreated();
      navigate(`/forms/${created.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteForm(api, deleteTarget.id);
      setDeleteTarget(null);
      onDeleted();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Your forms</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ErrorAlert error={actionError} className="mx-4" />
        {!forms ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : forms.length === 0 ? (
          <p className="px-4 pb-6 text-sm text-muted-foreground">
            No forms yet — create one, add fields, then place it on a page with the Form block.
            (Your built-in contact form keeps working without one.)
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Fields</th>
                <th className="px-4 py-3 font-medium">Submissions</th>
                <th className="px-4 py-3 font-medium">Last updated</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <tr key={form.id} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium">{form.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{form.fields.length}</td>
                  <td className="px-4 py-3 text-muted-foreground">{form.submission_count ?? 0}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatTimestamp(form.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/forms/${form.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete form"
                        onClick={() => setDeleteTarget(form)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={onCreateOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a form</DialogTitle>
            <DialogDescription>
              Give your form a name, then add fields in the builder. Show it on any page with the Form block.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-form-name">Name</Label>
              <Input
                id="new-form-name"
                value={newName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && create()}
                placeholder="e.g. Booking enquiry"
                maxLength={100}
                autoFocus
              />
            </div>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onCreateOpenChange(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={create} disabled={creating || !newName.trim()}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name}"?`}
        description={
          <>
            This deletes the form and its {deleteTarget?.submission_count ?? 0} submission
            {deleteTarget?.submission_count === 1 ? "" : "s"} (including uploaded files).
            Pages using this form show nothing in its place. This can't be undone.
          </>
        }
        confirmLabel="Delete form"
        busy={deleting}
        onConfirm={remove}
      />
    </Card>
  );
}
